import React from 'react';
import { Award, Globe, Headphones, Users, Clock, Lock, UserCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { generateStableId } from '../utils/keys';

const WhyChooseUs = () => {
  const { t } = useTranslation();
  const stats = [
    {
      icon: Award,
      number: '98%',
      label: t('approvalRate'),
      gradient: 'from-logo-cyan to-logo-cyan'
    },
    {
      icon: Globe,
      number: '50+',
      label: t('destinations'),
      gradient: 'from-cyan-500 to-blue-600'
    },
    {
      icon: Headphones,
      number: '24/7',
      label: t('expertSupport'),
      gradient: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Users,
      number: '25K+',
      label: t('visasProcessed'),
      gradient: 'from-indigo-500 to-purple-600'
    }
  ];

  const features = [
    {
      icon: Clock,
      title: t('fastEmbassyAppointments'),
      color: 'text-logo-blue'
    },
    {
      icon: Lock,
      title: t('secureDocumentHandling'),
      color: 'text-logo-cyan'
    },
    {
      icon: UserCheck,
      title: t('dedicatedVisaConsultant'),
      color: 'text-blue-600'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('whyChooseUs')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('whyChooseUsSubtitle')}
          </p>
          <p className="text-base text-gray-600 mt-2">
            {t('joinUAEResidents')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={`stat-${generateStableId(stat.label)}`}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 text-center group hover:scale-105"
              >
                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br ${stat.gradient} mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="w-8 h-8 text-white" strokeWidth={2} />
                </div>

                {/* Number */}
                <div className={`text-4xl lg:text-5xl font-bold bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent mb-2`}>
                  {stat.number}
                </div>

                {/* Label */}
                <div className="text-gray-600 font-semibold text-sm lg:text-base">
                  {stat.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Features List */}
        <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={`feature-${generateStableId(feature.title)}`}
                  className="flex items-center space-x-4 group"
                >
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Icon className={`w-7 h-7 ${feature.color}`} strokeWidth={2} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900">
                      {feature.title}
                    </h3>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
